import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext(null);

// Dutch translations
const nl = {
  // Navigation
  nav: {
    dashboard: 'Dashboard',
    bookkeeping: 'Boekhouding',
    vatReturns: 'BTW Aangiftes',
    bankStatements: 'Bankafschriften',
    invoices: 'Facturen',
    financialStatements: 'Financiële Overzichten',
    minutes: 'Notulen',
    capTable: 'Aandeelhouders',
    stockOptions: 'Medewerker Opties',
    settings: 'Instellingen'
  },
  // Common
  common: {
    save: 'Opslaan',
    cancel: 'Annuleren',
    delete: 'Verwijderen',
    edit: 'Bewerken',
    add: 'Toevoegen',
    search: 'Zoeken',
    filter: 'Filteren',
    export: 'Exporteren',
    import: 'Importeren',
    loading: 'Laden...',
    noData: 'Geen gegevens',
    confirm: 'Bevestigen',
    back: 'Terug',
    next: 'Volgende',
    submit: 'Verzenden',
    draft: 'Concept',
    sent: 'Verzonden',
    paid: 'Betaald',
    overdue: 'Achterstallig',
    total: 'Totaal',
    subtotal: 'Subtotaal',
    date: 'Datum',
    description: 'Omschrijving',
    amount: 'Bedrag',
    category: 'Categorie',
    status: 'Status',
    actions: 'Acties'
  },
  // Dashboard
  dashboard: {
    welcome: 'Welkom',
    overview: 'Overzicht',
    totalIncome: 'Totale Inkomsten',
    totalExpenses: 'Totale Uitgaven',
    netIncome: 'Netto Resultaat',
    openInvoices: 'Openstaande Facturen',
    vatDue: 'BTW te betalen',
    recentTransactions: 'Recente Transacties',
    upcomingDeadlines: 'Aankomende Deadlines'
  },
  // Bookkeeping
  bookkeeping: {
    newTransaction: 'Nieuwe Transactie',
    transactionDate: 'Transactiedatum',
    accountCode: 'Grootboekrekening',
    vatApplicable: 'BTW van toepassing',
    vatRate: 'BTW Tarief',
    income: 'Inkomsten',
    expense: 'Uitgaven'
  },
  // VAT
  vat: {
    quarter: 'Kwartaal',
    year: 'Jaar',
    vatCollected: 'Ontvangen BTW',
    vatPaid: 'Betaalde BTW',
    vatDue: 'Te betalen BTW',
    generate: 'Genereer Aangifte',
    submit: 'Aangifte Indienen',
    downloadPdf: 'Download PDF'
  },
  // Bank statements
  bankStatements: {
    uploadPdf: 'Upload PDF Bankafschrift',
    dragDrop: 'Sleep hier of klik om te uploaden',
    processing: 'Bezig met verwerken...',
    extractedTransactions: 'Gevonden Transacties',
    confirmBooking: 'Bevestig Boeking',
    applyVat: '21% BTW van toepassing?',
    bookTo: 'Boeken naar'
  },
  // Invoices
  invoices: {
    newInvoice: 'Nieuwe Factuur',
    invoiceNumber: 'Factuurnummer',
    client: 'Klant',
    issueDate: 'Factuurdatum',
    dueDate: 'Vervaldatum',
    items: 'Regelitems',
    addItem: 'Regel Toevoegen',
    markAsSent: 'Markeer als Verzonden',
    markAsPaid: 'Markeer als Betaald',
    preview: 'Voorbeeld'
  },
  // Financial statements
  statements: {
    balanceSheet: 'Balans',
    incomeStatement: 'Winst- en Verliesrekening',
    cashFlow: 'Kasstroomoverzicht',
    assets: 'Activa',
    liabilities: 'Passiva',
    equity: 'Eigen Vermogen',
    revenue: 'Omzet',
    expenses: 'Kosten',
    profit: 'Winst',
    loss: 'Verlies'
  },
  // Minutes
  minutes: {
    newMinutes: 'Nieuwe Notulen',
    meetingDate: 'Vergaderdatum',
    meetingType: 'Type Vergadering',
    attendees: 'Aanwezigen',
    summary: 'Samenvatting',
    generateMinutes: 'Genereer Notulen',
    boardMeeting: 'Bestuursvergadering',
    shareholderMeeting: 'Aandeelhoudersvergadering',
    generalMeeting: 'Algemene Vergadering'
  },
  // Cap table
  capTable: {
    shareholders: 'Aandeelhouders',
    shares: 'Aandelen',
    percentage: 'Percentage',
    shareClass: 'Aandelenklasse',
    common: 'Gewoon',
    preferred: 'Preferent',
    vesting: 'Vesting',
    vestingStart: 'Vesting Start',
    vestingMonths: 'Vesting Maanden',
    cliffMonths: 'Cliff Maanden'
  },
  // Stock options
  options: {
    employee: 'Medewerker',
    grantDate: 'Toekenningsdatum',
    strikePrice: 'Uitoefenprijs',
    vestedShares: 'Geveste Aandelen',
    exercised: 'Uitgeoefend',
    active: 'Actief',
    cancelled: 'Geannuleerd'
  },
  // Settings
  settings: {
    entitySettings: 'Entiteit Instellingen',
    companyName: 'Bedrijfsnaam',
    address: 'Adres',
    taxId: 'KVK Nummer',
    logo: 'Logo',
    bankAccount: 'Bankrekening',
    language: 'Taal'
  }
};

// English translations
const en = {
  nav: {
    dashboard: 'Dashboard',
    bookkeeping: 'Bookkeeping',
    vatReturns: 'VAT Returns',
    bankStatements: 'Bank Statements',
    invoices: 'Invoices',
    financialStatements: 'Financial Statements',
    minutes: 'Meeting Minutes',
    capTable: 'Cap Table',
    stockOptions: 'Stock Options',
    settings: 'Settings'
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    loading: 'Loading...',
    noData: 'No data',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    total: 'Total',
    subtotal: 'Subtotal',
    date: 'Date',
    description: 'Description',
    amount: 'Amount',
    category: 'Category',
    status: 'Status',
    actions: 'Actions'
  },
  dashboard: {
    welcome: 'Welcome',
    overview: 'Overview',
    totalIncome: 'Total Income',
    totalExpenses: 'Total Expenses',
    netIncome: 'Net Income',
    openInvoices: 'Open Invoices',
    vatDue: 'VAT Due',
    recentTransactions: 'Recent Transactions',
    upcomingDeadlines: 'Upcoming Deadlines'
  },
  bookkeeping: {
    newTransaction: 'New Transaction',
    transactionDate: 'Transaction Date',
    accountCode: 'Account Code',
    vatApplicable: 'VAT Applicable',
    vatRate: 'VAT Rate',
    income: 'Income',
    expense: 'Expense'
  },
  vat: {
    quarter: 'Quarter',
    year: 'Year',
    vatCollected: 'VAT Collected',
    vatPaid: 'VAT Paid',
    vatDue: 'VAT Due',
    generate: 'Generate Return',
    submit: 'Submit Return',
    downloadPdf: 'Download PDF'
  },
  bankStatements: {
    uploadPdf: 'Upload PDF Bank Statement',
    dragDrop: 'Drag and drop or click to upload',
    processing: 'Processing...',
    extractedTransactions: 'Extracted Transactions',
    confirmBooking: 'Confirm Booking',
    applyVat: 'Apply 21% VAT?',
    bookTo: 'Book to'
  },
  invoices: {
    newInvoice: 'New Invoice',
    invoiceNumber: 'Invoice Number',
    client: 'Client',
    issueDate: 'Issue Date',
    dueDate: 'Due Date',
    items: 'Line Items',
    addItem: 'Add Item',
    markAsSent: 'Mark as Sent',
    markAsPaid: 'Mark as Paid',
    preview: 'Preview'
  },
  statements: {
    balanceSheet: 'Balance Sheet',
    incomeStatement: 'Income Statement',
    cashFlow: 'Cash Flow Statement',
    assets: 'Assets',
    liabilities: 'Liabilities',
    equity: 'Equity',
    revenue: 'Revenue',
    expenses: 'Expenses',
    profit: 'Profit',
    loss: 'Loss'
  },
  minutes: {
    newMinutes: 'New Minutes',
    meetingDate: 'Meeting Date',
    meetingType: 'Meeting Type',
    attendees: 'Attendees',
    summary: 'Summary',
    generateMinutes: 'Generate Minutes',
    boardMeeting: 'Board Meeting',
    shareholderMeeting: 'Shareholder Meeting',
    generalMeeting: 'General Meeting'
  },
  capTable: {
    shareholders: 'Shareholders',
    shares: 'Shares',
    percentage: 'Percentage',
    shareClass: 'Share Class',
    common: 'Common',
    preferred: 'Preferred',
    vesting: 'Vesting',
    vestingStart: 'Vesting Start',
    vestingMonths: 'Vesting Months',
    cliffMonths: 'Cliff Months'
  },
  options: {
    employee: 'Employee',
    grantDate: 'Grant Date',
    strikePrice: 'Strike Price',
    vestedShares: 'Vested Shares',
    exercised: 'Exercised',
    active: 'Active',
    cancelled: 'Cancelled'
  },
  settings: {
    entitySettings: 'Entity Settings',
    companyName: 'Company Name',
    address: 'Address',
    taxId: 'Tax ID',
    logo: 'Logo',
    bankAccount: 'Bank Account',
    language: 'Language'
  }
};

const translations = { nl, en };

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('nl');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'nl' ? 'en' : 'nl');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
